import { useEffect, useState } from 'react';
import { C, DENSITIES, THEME_MODES } from '../lib/theme';
import { styles } from '../lib/styles';
import { fmtDateTime } from '../lib/util';
import {
  CASE_FILTERS,
  CLOCK_FORMATS,
  DATE_FORMATS,
  LANDING_PAGES,
  ROWS_PER_PAGE_CHOICES,
  resetPrefs,
  setPrefs,
  usePrefs,
} from '../lib/prefs';
import {
  NOTIFICATION_EVENTS,
  NOTIFICATION_EVENT_LABELS,
  fetchPreferences,
  savePreferences,
  type EmailPrefs,
  type NotificationEvent,
} from '../api/preferences';

// #399 — "Settings": the per-user surface, as opposed to pages/Settings.tsx
// which is org configuration (SLA windows, reason codes, external links).
//
// Two halves with different storage, and the page says which is which, because
// the difference is user-visible: display settings apply to this browser only,
// email settings follow the account everywhere.

// #436 — the events something in the portal actually produces today. The
// vocabulary is one list spanning staff and customer concerns (see
// src/lib/notification-prefs.ts in the API), and producers arrive one issue at a
// time, so a toggle can be genuinely stored and genuinely consulted while still
// having nothing to suppress.
//
// Saying so per row rather than hiding the rest: the preference IS recorded, so
// somebody who switches an unwired event off has expressed a real intent that
// applies the day its producer lands — hiding the control would silently discard
// that, and would also make each new producer a UI change rather than just a
// producer. What the reader must not conclude is that mail is being suppressed
// today, which is exactly what an unlabelled checkbox would tell them.
//
// Typed to the vocabulary, not to string: a mistyped event name here would
// otherwise be a Set entry that silently matches nothing, i.e. a wired event
// quietly relabelled "not sent yet" with no compiler complaint.
//
// PINNED to the producers (apps/api/src/lib/email-producers.ts) by Preferences.producers.test.ts (#660).
const WIRED_EVENTS = new Set<NotificationEvent>([
  'caseAssigned', 'caseStatusChanged',
  // #656 — a recorded vetting decision on a case you are assigned to, and the
  // SLA/cure deadlines on it (the cure-window reminder as it approaches, every
  // deadline kind as it passes).
  'caseDecision', 'slaDeadline',
  // #657 — a SHAFT or IP dispute opened or moved on a case you are assigned to,
  // and the outcome of that case's Settlement Platform delivery (completed, its
  // first failure, and the dead-letter that ends the retries).
  'disputeUpdate', 'settlementSync',
  // #658 — a customer filed an application. The only event here whose producer
  // is not a staff action at all: it fires on POST /api/v1/intake/submissions
  // and mails every enabled admin and analyst, because a submission has no
  // assigned analyst yet and somebody has to become one.
  'intakeSubmission',
]);

// WITH #658 THIS SET COVERS THE WHOLE VOCABULARY, so the marker below renders
// for nothing today. It is kept rather than deleted, and the reason is the
// failure it exists to prevent: the vocabulary is one shared list, the next
// event added to it lands on this page as a checkbox the moment it is declared,
// and the producer for it generally arrives in a later commit than the name
// does. Deleting the marker would make that window silent — a control that
// claims to suppress mail nobody is sending. Preferences.test.tsx asserts the
// coverage directly, so an event added without a producer fails there rather
// than shipping unmarked.

const LANDING_PAGE_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  intake: 'Intake Queue',
  onboarding: 'Applications',
  cases: 'Vetting Cases',
  notifications: 'Notifications',
  'cure-appeals': 'Cure & Appeals',
  revets: 'Re-Vet Schedule',
  settlement: 'Settlement Platform',
  changes: 'Change Tracking',
  'ip-disputes': 'IP & SHAFT Disputes',
};

const THEME_LABELS: Record<string, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'Match my system',
};

const DENSITY_LABELS: Record<string, string> = {
  comfortable: 'Comfortable',
  compact: 'Compact',
};

const DATE_FORMAT_LABELS: Record<string, string> = {
  us: 'Mar 12, 2026',
  iso: '2026-03-12',
  long: 'March 12, 2026',
};

const CLOCK_LABELS: Record<string, string> = {
  '12h': '12-hour (9:30 PM)',
  '24h': '24-hour (21:30)',
};

const CASE_FILTER_LABELS: Record<string, string> = {
  all: 'All cases',
  mine: 'Assigned to me',
};

// The zones this deployment's staff actually work in, plus UTC for anyone
// reconciling against CTIA timestamps. A full IANA list is ~600 entries of
// which none of the rest are useful here, and the field accepts only what
// coercePrefs validates anyway.
const TIMEZONES: Array<[string, string]> = [
  ['', 'Use this device’s time zone'],
  ['UTC', 'UTC'],
  ['America/New_York', 'Eastern (America/New_York)'],
  ['America/Chicago', 'Central (America/Chicago)'],
  ['America/Denver', 'Mountain (America/Denver)'],
  ['America/Phoenix', 'Arizona (America/Phoenix)'],
  ['America/Los_Angeles', 'Pacific (America/Los_Angeles)'],
];

function Field({ label, hint, children }: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label style={{ fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 4 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function Choice<T extends string | number>({ label, hint, value, options, onChange }: {
  label: string;
  hint?: string;
  value: T;
  options: ReadonlyArray<readonly [T, string]>;
  onChange: (v: T) => void;
}) {
  return (
    <Field label={label} hint={hint}>
      <select
        aria-label={label}
        style={{ ...styles.select, width: '100%' }}
        value={String(value)}
        onChange={e => {
          const raw = e.target.value;
          const match = options.find(([v]) => String(v) === raw);
          if (match) onChange(match[0]);
        }}
      >
        {options.map(([v, text]) => <option key={String(v)} value={String(v)}>{text}</option>)}
      </select>
    </Field>
  );
}

const labelled = <T extends string>(values: readonly T[], labels: Record<string, string>) =>
  values.map(v => [v, labels[v] ?? v] as const);

export function Preferences() {
  const prefs = usePrefs();

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 20 }}>Settings</h2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
        <div style={styles.card}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4 }}>Display</h3>
          <p style={{ fontSize: 11, color: C.textMuted, marginTop: 0, marginBottom: 16 }}>
            Saved in this browser on this device. Signing in elsewhere gives you the defaults again.
          </p>
          <div style={{ display: 'grid', gap: 14 }}>
            <Choice
              label="Theme"
              value={prefs.theme}
              options={labelled(THEME_MODES, THEME_LABELS)}
              onChange={theme => setPrefs({ theme })}
            />
            <Choice
              label="Table density"
              value={prefs.density}
              options={labelled(DENSITIES, DENSITY_LABELS)}
              onChange={density => setPrefs({ density })}
            />
            <Choice
              label="Rows per page"
              hint="Applies to the case and queue lists."
              value={prefs.rowsPerPage}
              options={ROWS_PER_PAGE_CHOICES.map(n => [n, String(n)] as const)}
              onChange={rowsPerPage => setPrefs({ rowsPerPage })}
            />
            <Choice
              label="Time zone"
              hint={`Timestamps currently read: ${fmtDateTime('2026-03-12T21:30:00Z')}`}
              value={prefs.timezone}
              options={TIMEZONES.map(([v, t]) => [v, t] as const)}
              onChange={timezone => setPrefs({ timezone })}
            />
            <Choice
              label="Date format"
              value={prefs.dateFormat}
              options={labelled(DATE_FORMATS, DATE_FORMAT_LABELS)}
              onChange={dateFormat => setPrefs({ dateFormat })}
            />
            <Choice
              label="Clock"
              value={prefs.clockFormat}
              options={labelled(CLOCK_FORMATS, CLOCK_LABELS)}
              onChange={clockFormat => setPrefs({ clockFormat })}
            />
            <Choice
              label="Start on"
              hint="The page the portal opens after you sign in."
              value={prefs.landingPage}
              options={labelled(LANDING_PAGES, LANDING_PAGE_LABELS)}
              onChange={landingPage => setPrefs({ landingPage })}
            />
            <Choice
              label="Default case list"
              hint="How the Vetting Cases page is scoped when you open it."
              value={prefs.caseFilter}
              options={labelled(CASE_FILTERS, CASE_FILTER_LABELS)}
              onChange={caseFilter => setPrefs({ caseFilter })}
            />
            <div>
              <button
                type="button"
                style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                onClick={() => resetPrefs()}
              >
                Reset display settings
              </button>
            </div>
          </div>
        </div>

        <EmailPreferencesCard />
      </div>
    </div>
  );
}

// Server-side half. Kept a separate component so its loading and save states
// cannot make the display settings — which need no network at all — wait on a
// request or fail with one.
function EmailPreferencesCard() {
  const [prefs, setEmailPrefs] = useState<EmailPrefs | null>(null);
  const [status, setStatus] = useState<{ kind: 'idle' | 'saving' | 'saved' | 'error'; message?: string }>({ kind: 'idle' });
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    fetchPreferences()
      .then(res => { if (active) setEmailPrefs(res.emailPrefs); })
      .catch((err: unknown) => {
        if (active) setLoadError(err instanceof Error ? err.message : String(err));
      });
    return () => { active = false; };
  }, []);

  // A full replacement on every toggle, matching the API. Optimistic: the
  // checkbox moves immediately and reverts if the write fails, because a
  // checkbox that waits for a round-trip before moving reads as broken.
  const toggle = async (event: string, next: boolean) => {
    if (!prefs) return;
    const previous = prefs;
    const desired = { ...prefs, [event]: next } as EmailPrefs;
    setEmailPrefs(desired);
    setStatus({ kind: 'saving' });
    try {
      const res = await savePreferences(desired);
      setEmailPrefs(res.emailPrefs);
      setStatus({ kind: 'saved' });
    } catch (err) {
      setEmailPrefs(previous);
      setStatus({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  };

  return (
    <div style={styles.card}>
      <h3 style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4 }}>Email notifications</h3>
      {/* #436 — the tense changed here because the facts did. #399 shipped the
          preference and the worker's consult but no producer, so this paragraph
          said "applied as staff notifications are added": a promise about the
          future, because a present-tense claim would have been one the reader
          could disprove by switching something off and watching mail arrive.
          Two events now have producers, so the first sentence is true today —
          and the ones that do not are marked individually below rather than
          hedged over the whole list, because a blanket hedge makes the two real
          controls look ornamental too. */}
      <p style={{ fontSize: 11, color: C.textMuted, marginTop: 0, marginBottom: 16 }}>
        Saved to your account rather than to this device, and applied wherever you sign in.
        Switching one off stops that email; sign-in and account emails are always sent.
      </p>

      {loadError && (
        <div style={{ fontSize: 12, color: C.danger, background: C.dangerBg, padding: 12, borderRadius: 8 }}>
          Could not load your notification settings: {loadError}
        </div>
      )}
      {!loadError && !prefs && <div style={{ fontSize: 12, color: C.textMuted }}>Loading…</div>}

      {prefs && (
        <>
          <div style={{ display: 'grid', gap: 6 }}>
            {NOTIFICATION_EVENTS.map(event => (
              <label key={event} style={{ ...styles.checkItem, cursor: 'pointer', color: C.text }}>
                <input
                  type="checkbox"
                  checked={prefs[event] !== false}
                  onChange={e => { void toggle(event, e.target.checked); }}
                />
                <span>
                  {NOTIFICATION_EVENT_LABELS[event]}
                  {!WIRED_EVENTS.has(event) && (
                    <span style={{ color: C.textMuted }}> — not sent yet</span>
                  )}
                </span>
              </label>
            ))}
          </div>
          <div
            role="status"
            style={{ fontSize: 11, marginTop: 12, color: status.kind === 'error' ? C.danger : C.textMuted }}
          >
            {status.kind === 'saving' && 'Saving…'}
            {status.kind === 'saved' && 'Saved.'}
            {status.kind === 'error' && `Could not save: ${status.message}`}
          </div>
        </>
      )}
    </div>
  );
}
